import EmptySection from './EmptySection';
import styles from './ProjectTimeline.module.css';
import { useTranslation } from 'react-i18next';
import DescriptionIcon from '@mui/icons-material/Description';

const GRAY_LINE = '#e5e7eb';

const hexToRgba = (hex, alpha) => {
  if (!hex || !hex.startsWith('#') || hex.length < 7) {
    return `rgba(156, 163, 175, ${alpha})`;
  }

  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const ProjectTimeline = ({ phases, onPhaseProgressClick, onPhaseClick }) => {
  const { t } = useTranslation();
  const getConnectorStyle = (phase, nextPhase) => {
    if (!phase?.prikaz || !nextPhase?.prikaz) {
      return { background: GRAY_LINE };
    }

    if (phase.procenat > 0) {
      return { background: phase.boja };
    }

    if (nextPhase.procenat > 0) {
      return { background: nextPhase.boja };
    }

    return { background: GRAY_LINE };
  };

  return (
    <div className={styles.card}>
      <h3 className={styles.title}>Project Timeline</h3>
      {!phases?.length ? (
        <EmptySection />
      ) : (
        <div className={styles.timeline}>
          {phases.map((phase, index) => {
            const isLast = index === phases.length - 1;
            const nextPhase = phases[index + 1];
            const stepNumber = String(index + 1).padStart(2, '0');
            const isDisabled = !phase.prikaz;
            const isMuted =
              isDisabled || !phase.procenat || Number(phase.procenat) === 0;

            const isProgressClickable = !isDisabled && !!onPhaseProgressClick;
            const isPhaseClickable = Boolean(onPhaseClick) && !isDisabled;

            return (
              <div
                key={phase.id}
                className={`${styles.step} ${isMuted ? styles.stepMuted : ''} ${
                  isDisabled ? styles.stepDisabled : ''
                }`}
              >
                <div
                  className={`${styles.stepPercent} ${
                    isMuted ? styles.stepPercentMuted : ''
                  } ${isProgressClickable ? styles.stepPercentClickable : ''}`}
                  style={
                    isMuted
                      ? undefined
                      : {
                          color: phase.boja,
                          backgroundColor: hexToRgba(phase.boja, 0.15),
                        }
                  }
                  onClick={
                    isProgressClickable
                      ? () => onPhaseProgressClick(phase)
                      : undefined
                  }
                  role={isProgressClickable ? 'button' : undefined}
                  tabIndex={isProgressClickable ? 0 : undefined}
                  onKeyDown={
                    isProgressClickable
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onPhaseProgressClick(phase);
                          }
                        }
                      : undefined
                  }
                >
                  {phase.procenat}%
                </div>

                <div className={styles.circleRow}>
                  {!isLast && (
                    <div className={styles.stepConnector}>
                      <div
                        className={styles.stepConnectorFill}
                        style={getConnectorStyle(phase, nextPhase)}
                      />
                    </div>
                  )}

                  <div
                    className={`${styles.circle} ${
                      isMuted ? styles.circleMuted : ''
                    } ${isPhaseClickable ? styles.circleClickable : ''}`}
                    style={{
                      background: isMuted ? undefined : phase.boja,
                    }}
                    onClick={
                      isPhaseClickable ? () => onPhaseClick(phase) : undefined
                    }
                    role={isPhaseClickable ? 'button' : undefined}
                    tabIndex={isPhaseClickable ? 0 : undefined}
                    onKeyDown={
                      isPhaseClickable
                        ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onPhaseClick(phase);
                            }
                          }
                        : undefined
                    }
                  >
                    {stepNumber}
                  </div>
                </div>

                <div
                  className={`${styles.stepLabel} ${
                    isPhaseClickable ? styles.stepLabelClickable : ''
                  }`}
                  onClick={
                    isPhaseClickable ? () => onPhaseClick(phase) : undefined
                  }
                  role={isPhaseClickable ? 'button' : undefined}
                  tabIndex={isPhaseClickable ? 0 : undefined}
                  onKeyDown={
                    isPhaseClickable
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onPhaseClick(phase);
                          }
                        }
                      : undefined
                  }
                >
                  {t(`projectPhases.${phase.key}`)}
                </div>

                <DescriptionIcon
                  className={`${styles.fileIcon} ${
                    phase.imaFajl
                      ? styles.fileIconEnabled
                      : styles.fileIconDisabled
                  }`}
                  fontSize="small"
                  titleAccess={phase.imaFajl ? 'Has files' : 'No files'}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProjectTimeline;
